// Theme preference: light | dark | system. Persisted on-device in
// localStorage (small, synchronous — same convention as the other
// `agg:<surface>:v<int>` keys). "system" means "follow the OS via
// prefers-color-scheme" and is the default.
//
// Applied by setting (or clearing) `data-theme` on <html>:
//   - "light"  → <html data-theme="light">  (force light)
//   - "dark"   → <html data-theme="dark">   (force dark)
//   - "system" → attribute removed; tokens.css falls back to the
//                @media (prefers-color-scheme) block.
// tokens.css is written so the attribute always wins over the media
// query, which is what makes "force light on a dark OS" actually work.

import { useState } from "preact/hooks";

export type ThemeChoice = "light" | "dark" | "system";

const STORAGE_KEY = "agg:theme:v1";

/** Read the stored choice. Defaults to "system" (and on any malformed /
 *  unavailable storage). Never throws. */
export function loadThemeChoice(): ThemeChoice {
  if (typeof localStorage === "undefined") return "system";
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
    return "system";
  } catch {
    return "system";
  }
}

/** Reflect a choice onto <html data-theme>. "system" removes the attribute
 *  so the OS prefers-color-scheme takes over. Also syncs the browser/PWA
 *  chrome color (`<meta name="theme-color">`) to the resolved page
 *  background, so a manual override flips the chrome too — the static
 *  per-scheme metas in index.html only track the OS, not the toggle. */
export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === "system") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", choice);
  }
  syncThemeColorMeta();
}

/** Point a single dynamic `<meta name="theme-color" data-dynamic>` at the
 *  computed `--color-bg`, so the address bar / PWA title bar match the
 *  active theme regardless of how it was chosen. The static media-keyed
 *  metas in index.html remain as the no-JS / first-paint fallback; this
 *  dynamic one is appended last so it wins. No-op outside the DOM. */
function syncThemeColorMeta(): void {
  if (typeof document === "undefined") return;
  const bg = getComputedStyle(document.documentElement)
    .getPropertyValue("--color-bg")
    .trim();
  if (!bg) return;
  let meta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"][data-dynamic]',
  );
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    meta.setAttribute("data-dynamic", "");
    document.head.appendChild(meta);
  }
  meta.content = bg;
}

/** Persist + apply in one call. Best-effort persistence (private mode
 *  keeps an in-memory session for the current page). */
export function setThemeChoice(choice: ThemeChoice): void {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // storage unavailable — apply for this session only.
  }
  applyTheme(choice);
}

/** Apply the stored choice. Called once at startup from main.tsx, before
 *  first paint, so there's no flash of the wrong theme. */
export function applyStoredTheme(): void {
  applyTheme(loadThemeChoice());
}

/** React hook: the current choice + a setter that persists and applies.
 *  The setter owns apply+persist (via `setThemeChoice`); there is no
 *  separate apply effect, so `applyTheme` runs exactly once per change. */
export function useTheme(): [ThemeChoice, (next: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(() => loadThemeChoice());
  const set = (next: ThemeChoice) => {
    setChoice(next);
    setThemeChoice(next);
  };
  return [choice, set];
}
